/** @format */
import { inject, Injectable } from '@angular/core';
import { ValidatablePlan } from 'common-typescript';
import {
    CourseUnit,
    CourseUnitSelection,
    CourseUnitTiming,
    CustomStudyDraft,
    LocalId,
    Module,
    OtmId,
    Plan,
    StudyPeriodLocator,
} from 'common-typescript/types';
import { cloneDeep } from 'lodash-es';
import { combineLatest, Observable, of, switchMap } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { PlanEntityService } from 'sis-components';
import { TimingTemplateSelection } from '../timing.types';
import { SelectedPlanService } from './selected-plan.service';
import { SelectedValidatablePlanService } from './selected-validatable-plan.service';
import { UntimedCourseUnitService } from './untimed-course-unit.service';

@Injectable()
export class TimingActionService {
    private readonly selectedPlanService = inject(SelectedPlanService);
    private readonly planEntityService = inject(PlanEntityService);
    private readonly selectedValidatablePlanService: SelectedValidatablePlanService = inject(SelectedValidatablePlanService);
    private readonly untimedCourseUnitService: UntimedCourseUnitService = inject(UntimedCourseUnitService);

    private readonly selectedPlan$: Observable<Plan> = this.selectedPlanService.selectedPlan$.pipe(
        take(1),
        filter(plan => plan !== null),
    );

    /**
     *  Remove all timings from plan that is active in {@link SelectedPlanService}
     *  @returns Observable<void>, when subscribed to removes the selectedPlan timings, then completes.
     */
    removeSelectedPlanTimings(): Observable<void> {
        return this.selectedPlan$.pipe(
            switchMap(plan => this.planEntityService.updateMyPlan(TimingActionService.clearPlanTimings(plan))),
            map<Plan, void>(() => undefined),
        );
    }

    /**
     * Set timings for course units and custom study drafts in the selected plan ({@link SelectedPlanService}) for the given study period.
     * Also removes timings for course units and custom study drafts that are not in the given lists for the given study period.
     *
     * @param studyPeriodLocator The study period locator where the timings are to be set.
     * @param courseUnitIds IDs of the course units to be timed for the given study period.
     * @param customStudyDraftIds IDs of the custom study drafts to be timed for the given study period.
     */
    saveSelectedPlanTimings(
        studyPeriodLocator: StudyPeriodLocator,
        courseUnitIds: ReadonlySet<OtmId>,
        customStudyDraftIds: ReadonlySet<LocalId>,
    ): Observable<void> {
        return this.selectedPlan$.pipe(
            map(plan => TimingActionService.setPlanTimings(plan, studyPeriodLocator, courseUnitIds, customStudyDraftIds)),
            switchMap(plan => this.planEntityService.updateMyPlan(plan)),
            map<Plan, void>(() => undefined),
        );
    }

    /**
     * Add timings for a course unit in the selected plan ({@link SelectedPlanService}) for the given study periods.
     *  Only adds non-existent timings. If timing already exists for given Cu and period, or Cu is not in plan, does nothing for that timing.
     *
     * @param studyPeriodLocators The study period locators where the timings are to be added.
     * @param courseUnitId ID of the course unit to be timed for the given study periods.
     *  @returns void {@link Observable}
     */
    addNewCourseUnitTimingForPeriods(studyPeriodLocators: ReadonlySet<StudyPeriodLocator>, courseUnitId: OtmId): Observable<void> {
        return this.selectedPlan$.pipe(
            map(plan => TimingActionService.addNewCuTimingForPeriods(plan, studyPeriodLocators, courseUnitId)),
            switchMap(plan => this.planEntityService.updateMyPlan(plan)),
            map<Plan, void>(() => undefined),
        );
    }

    /**
     * Add timings for a custom study draft in the selected plan ({@link SelectedPlanService}) for the given study periods.
     *  Only adds non-existent timings. If timing already exists for given CSD and period, or CSD is not in plan, does nothing for that timing.
     *
     * @param studyPeriodLocators The study period locators where the timings are to be added.
     * @param customStudyDraftId ID of the custom study draft to be timed for the given study periods.
     *  @returns void {@link Observable}
     */
    addNewCustomStudyDraftTimingForPeriods(
        studyPeriodLocators: ReadonlySet<StudyPeriodLocator>,
        customStudyDraftId: LocalId,
    ): Observable<void> {
        return this.selectedPlan$.pipe(
            map(plan => TimingActionService.addNewCsdTimingForPeriods(plan, studyPeriodLocators, customStudyDraftId)),
            switchMap(plan => this.planEntityService.updateMyPlan(plan)),
            map<Plan, void>(() => undefined),
        );
    }

    /**
     * Adds timings from the given templates to the current selected plan ({@link SelectedPlanService.selectedPlan$}),
     * if one is selected.
     *
     * NOTE! {@link selections} must not contain more than one template per {@link Module},
     * and each {@link TimingTemplateSelection.timingTemplate} must belong to {@link TimingTemplateSelection.module}.
     *
     * The returned {@link Observable} always emits one value and then completes.
     */
    introduceTimingTemplatesToPlan(selections: readonly TimingTemplateSelection[]): Observable<void> {
        return combineLatest([
            this.selectedValidatablePlanService.selectedValidatablePlan$,
            // We don't want to use ValidatablePlan.plan, because it's not the actual plan in DB,
            // so let's get the real plan from SelectedPlanService.
            this.selectedPlanService.selectedPlan$,
        ]).pipe(
            // synchronize streams simply be checking that plan IDs match (or both plans are null)
            filter(([validatablePlan, plan]: [ValidatablePlan | null, Plan | null]) => validatablePlan?.plan.id === plan?.id),
            take(1),
            switchMap(([validatablePlan, plan]: [ValidatablePlan | null, Plan | null]) => {
                if (!validatablePlan || !plan) {
                    return of(undefined);
                }

                const planToSave: Plan | null = this.introduceTimingTemplates(validatablePlan, plan, selections);
                if (!planToSave) {
                    // no changes to save
                    return of(undefined);
                }

                // prettier-ignore
                return this.planEntityService.updateMyPlan(planToSave).pipe(
                    map<Plan, void>(() => undefined),
                );
            }),
        );
    }

    private static clearPlanTimings(plan: Plan): Plan {
        const courseUnitSelections = plan.courseUnitSelections.map(courseUnitSelection =>
            courseUnitSelection.plannedPeriods?.length
                ? <CourseUnitSelection>{ ...courseUnitSelection, plannedPeriods: [] }
                : courseUnitSelection,
        );
        const customStudyDrafts = plan.customStudyDrafts.map(customStudyDraft =>
            customStudyDraft.plannedPeriods?.length ? <CustomStudyDraft>{ ...customStudyDraft, plannedPeriods: [] } : customStudyDraft,
        );
        return <Plan>{ ...plan, courseUnitSelections, customStudyDrafts };
    }

    private static setPlanTimings(
        plan: Plan,
        studyPeriodLocator: StudyPeriodLocator,
        courseUnitIds: ReadonlySet<OtmId>,
        customStudyDraftIds: ReadonlySet<LocalId>,
    ): Plan {
        const planToBeModified = cloneDeep(plan);

        // Course unit selections
        planToBeModified.courseUnitSelections.forEach(courseUnitSelection => {
            const timedAlready = courseUnitSelection.plannedPeriods?.includes(studyPeriodLocator) || false;
            const shouldBeTimed = courseUnitIds.has(courseUnitSelection.courseUnitId);
            if (timedAlready && !shouldBeTimed) {
                // remove timing
                courseUnitSelection.plannedPeriods.splice(courseUnitSelection.plannedPeriods.indexOf(studyPeriodLocator), 1);
            } else if (!timedAlready && shouldBeTimed) {
                // add timing
                courseUnitSelection.plannedPeriods.push(studyPeriodLocator);
            }
        });

        // Custom study drafts
        planToBeModified.customStudyDrafts.forEach(customStudyDraft => {
            const timedAlready = customStudyDraft.plannedPeriods?.includes(studyPeriodLocator) || false;
            const shouldBeTimed = customStudyDraftIds.has(customStudyDraft.id);
            if (timedAlready && !shouldBeTimed) {
                // remove timing
                customStudyDraft.plannedPeriods.splice(customStudyDraft.plannedPeriods.indexOf(studyPeriodLocator), 1);
            } else if (!timedAlready && shouldBeTimed) {
                // add timing
                customStudyDraft.plannedPeriods.push(studyPeriodLocator);
            }
        });

        return planToBeModified;
    }

    /**
     * Add timings that are not already timed to {@link Plan.courseUnitSelections}.
     *  Does nothing, if courseUnitIdToAdd is NOT in the plan.
     *  or {@link StudyPeriodLocator}s for given courseUnit is already timed
     */
    private static addNewCuTimingForPeriods(
        plan: Plan,
        studyPeriodLocators: ReadonlySet<StudyPeriodLocator>,
        courseUnitIdToAdd: OtmId,
    ): Plan {
        const planToBeModified = cloneDeep(plan);
        const courseUnitSelection: CourseUnitSelection | null =
            planToBeModified.courseUnitSelections?.find(cuSelection => cuSelection.courseUnitId === courseUnitIdToAdd) ?? null;
        const periodsNotTimed: ReadonlySet<StudyPeriodLocator> = studyPeriodLocators.difference(
            new Set(courseUnitSelection?.plannedPeriods),
        );
        if (courseUnitSelection === null || periodsNotTimed.size === 0) {
            // no changes made, return same plan
            return planToBeModified;
        }

        if (courseUnitSelection.plannedPeriods === null) {
            courseUnitSelection.plannedPeriods = [];
        }

        courseUnitSelection.plannedPeriods.push(...periodsNotTimed);
        return planToBeModified;
    }

    /**
     * Add timings (in-place) that are not already timed to {@link Plan.customStudyDrafts}.
     *  Does nothing, if customStudyDraftIdToAdd is NOT in the plan.
     *  or {@link StudyPeriodLocator}s for given customStudyDraft is already timed
     */
    private static addNewCsdTimingForPeriods(
        plan: Plan,
        studyPeriodLocators: ReadonlySet<StudyPeriodLocator>,
        customStudyDraftIdToAdd: LocalId,
    ): Plan {
        const planToBeModified = cloneDeep(plan);
        const customStudyDraft: CustomStudyDraft | null =
            planToBeModified.customStudyDrafts?.find(csd => csd.id === customStudyDraftIdToAdd) ?? null;
        const periodsNotTimed: ReadonlySet<StudyPeriodLocator> = studyPeriodLocators.difference(new Set(customStudyDraft?.plannedPeriods));
        if (customStudyDraft === null || periodsNotTimed.size === 0) {
            // no changes made, return same plan
            return planToBeModified;
        }

        if (customStudyDraft.plannedPeriods === null) {
            customStudyDraft.plannedPeriods = [];
        }

        customStudyDraft.plannedPeriods.push(...periodsNotTimed);
        return planToBeModified;
    }

    /**
     * Returns null if templates didn't have an effect.
     */
    private introduceTimingTemplates(
        validatablePlan: ValidatablePlan,
        plan: Plan,
        selections: readonly TimingTemplateSelection[],
    ): Plan | null {
        const plannedPeriodsPerCourseUnitId = new Map<OtmId, Set<StudyPeriodLocator>>();
        for (const selection of selections) {
            this.introduceTimingTemplate(validatablePlan, selection, plannedPeriodsPerCourseUnitId);
        }

        if (!plannedPeriodsPerCourseUnitId.size) {
            // no changes
            return null;
        }

        // there are some actual changes --> clone the old plan and set new timings for selected course units found in templates
        const planClone: Plan = cloneDeep(plan);

        for (const courseUnitSelection of planClone.courseUnitSelections) {
            const plannedPeriods: Set<StudyPeriodLocator> | undefined = plannedPeriodsPerCourseUnitId.get(courseUnitSelection.courseUnitId);
            if (plannedPeriods) {
                courseUnitSelection.plannedPeriods = plannedPeriods.values().toArray();
            }
        }

        return planClone;
    }

    private introduceTimingTemplate(
        validatablePlan: ValidatablePlan,
        selection: TimingTemplateSelection,
        plannedPeriodsPerCourseUnitId: Map<OtmId, Set<StudyPeriodLocator>>,
    ): void {
        const nonEmptyCourseUnitTimings: CourseUnitTiming[] | undefined = selection.timingTemplate.courseUnitTimings?.filter(
            courseUnitTiming => !!courseUnitTiming.courseUnitGroupId && !!courseUnitTiming.plannedPeriods?.length,
        );
        if (!nonEmptyCourseUnitTimings?.length) {
            return;
        }

        const untimedCourseUnits: ReadonlySet<CourseUnit> = this.untimedCourseUnitService.getUntimedCourseUnitsForModuleRecursively(
            selection.module,
            validatablePlan,
        );
        if (!untimedCourseUnits.size) {
            return;
        }

        const untimedCourseUnitsPerGroupId: Partial<Record<OtmId, CourseUnit[]>> = groupCourseUnitsByGroupId(untimedCourseUnits);

        for (const nonEmptyCourseUnitTiming of nonEmptyCourseUnitTimings) {
            TimingActionService.introduceCourseUnitTiming(
                nonEmptyCourseUnitTiming,
                untimedCourseUnitsPerGroupId,
                plannedPeriodsPerCourseUnitId,
            );
        }
    }

    private static introduceCourseUnitTiming(
        nonEmptyCourseUnitTiming: CourseUnitTiming,
        untimedCourseUnitsPerGroupId: Partial<Record<OtmId, CourseUnit[]>>,
        plannedPeriodsPerCourseUnitId: Map<OtmId, Set<StudyPeriodLocator>>,
    ): void {
        const courseUnitsToTime: CourseUnit[] | undefined = untimedCourseUnitsPerGroupId[nonEmptyCourseUnitTiming.courseUnitGroupId];
        if (!courseUnitsToTime) {
            return;
        }

        for (const courseUnitToTime of courseUnitsToTime) {
            const plannedPeriods: Set<StudyPeriodLocator> | undefined = plannedPeriodsPerCourseUnitId.get(courseUnitToTime.id);
            if (plannedPeriods) {
                // This should be probably rare but (in the data model) possible.
                // Let's just combine planned periods of each course unit timing.
                nonEmptyCourseUnitTiming.plannedPeriods.forEach(plannedPeriod => plannedPeriods.add(plannedPeriod));
            } else {
                plannedPeriodsPerCourseUnitId.set(
                    courseUnitToTime.id,
                    new Set<StudyPeriodLocator>(nonEmptyCourseUnitTiming.plannedPeriods),
                );
            }
        }
    }
}

/**
 * For now, replaces {@link Object.groupBy}, that is not supported by older browsers, and whose polyfill would require a newer core-js.
 */
function groupCourseUnitsByGroupId(courseUnits: Iterable<CourseUnit>): Partial<Record<OtmId, CourseUnit[]>> {
    const result: Record<OtmId, CourseUnit[]> = {};
    for (const courseUnit of courseUnits) {
        let groupIdResult: CourseUnit[] | undefined = result[courseUnit.groupId];
        if (!groupIdResult) {
            groupIdResult = [];
            result[courseUnit.groupId] = groupIdResult;
        }
        groupIdResult.push(courseUnit);
    }
    return result;
}
